import { cn, deriveTrackName } from "@design-system/utils";
import { PanelLeftOpen } from "lucide-react";
import * as React from "react";

import { useIsMobile } from "../../hooks/use-mobile";
import { useSidebarState } from "../../hooks/use-sidebar-state";
import { SidebarTriggerButton } from "../sidebar";

function PageHeader({ className, ...props }: React.ComponentProps<"header">) {
  return (
    <header
      data-slot="page-header"
      className={cn(
        "box-border flex w-full flex-col gap-6 md:gap-8",
        className
      )}
      {...props}
    />
  );
}

function PageHeaderContent({
  className,
  children,
  storageKey,
  ...props
}: React.ComponentProps<"div"> & {
  storageKey: string;
}) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useSidebarState(storageKey);

  const isTriggerVisible = !isOpen || isMobile;

  return (
    <div
      data-slot="page-header-content"
      className={cn("flex h-11 items-center gap-1 rounded-lg p-2", className)}
      {...props}
    >
      {isTriggerVisible && (
        <SidebarTriggerButton
          className="mr-2"
          onClick={() => setIsOpen((prev) => !prev)}
          {...deriveTrackName(props, "-sidebar-trigger")}
        >
          <PanelLeftOpen />
        </SidebarTriggerButton>
      )}
      {children}
    </div>
  );
}

function PageHeaderActions({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="page-header-actions"
      className={cn("ml-auto flex items-center gap-2", className)}
      {...props}
    />
  );
}

function PageHeaderNav({ className, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      aria-label="Page navigation"
      data-slot="page-header-nav"
      className={cn("w-full", className)}
      {...props}
    />
  );
}

export { PageHeader, PageHeaderContent, PageHeaderActions, PageHeaderNav };
